import { useEffect, useState } from 'react'
import type { Partner } from '@shared/types'
import { formatUKDate } from '@shared/dates'
import { SlideOver } from '../components/SlideOver.js'
import { RaisePurchaseOrder } from '../components/RaisePurchaseOrder.js'
import {
  fetchSupplierQuote, fetchSupplierQuotes, refreshPurchaseOrderStatus, supplierQuoteFileUrl,
  type PurchaseOrderView, type SupplierQuoteLineRow, type SupplierQuoteRow,
} from '../lib/api.js'

/**
 * Supplier quotes (decision 211) — every distributor quote imported into the
 * portal: reference, the customer quote it produced, the margin it was
 * priced at, the price-protection date, and the original workbook. Opening
 * an entry shows the lines as the sheet had them next to what the quote line
 * says NOW (qty and sell move on in the builder). Decision 213 (phase B):
 * the Xero draft purchase order is raised from here — the PO column shows
 * the live one per quote, "Raise PO" opens the preview panel.
 *
 * ⚠️ Staff-only by nav gate AND by the API — every figure is cost.
 */

const gbp = (n: number | null) => n == null ? '—' : `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function SupplierQuotes({ partner, onOpen }: {
  partner: Partner | null
  onOpen: (quoteId: string) => void
}) {
  const [rows, setRows] = useState<SupplierQuoteRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<{ record: SupplierQuoteRow; lines: SupplierQuoteLineRow[] } | null>(null)
  const [po, setPo] = useState<SupplierQuoteRow | null>(null)
  const [checking, setChecking] = useState<string | null>(null)

  // One live PO per quote + distributor: every register row for that pair shows it.
  const setRowPo = (id: string, next: PurchaseOrderView) => setRows((rs) => {
    const src = rs?.find((x) => x.id === id)
    if (!rs || !src) return rs
    return rs.map((r) => (r.quoteId != null && r.quoteId === src.quoteId && r.distributor === src.distributor ? { ...r, po: next } : r))
  })
  const checkStatus = async (r: SupplierQuoteRow) => {
    setChecking(r.id); setError(null)
    try { const res = await refreshPurchaseOrderStatus(r.id); setRowPo(r.id, res.po) } catch (e) { setError((e as Error).message) } finally { setChecking(null) }
  }

  useEffect(() => {
    fetchSupplierQuotes().then((r) => setRows(r.supplierQuotes)).catch((e: Error) => setError(e.message))
  }, [partner?.id])

  if (error && !rows) return <p className="pill pill--stop">{error}</p>
  if (!rows) return <p className="muted">Loading…</p>

  const q = query.trim().toLowerCase()
  const shown = q
    ? rows.filter((r) => `${r.reference} ${r.ccwReference ?? ''} ${r.quoteNumber ?? ''} ${r.quoteTitle ?? ''} ${r.createdBy} ${r.distributor}`.toLowerCase().includes(q))
    : rows
  const today = new Date().toISOString().slice(0, 10)

  const openOne = async (id: string) => {
    try { const r = await fetchSupplierQuote(id); setOpen({ record: r.supplierQuote, lines: r.lines }) } catch (e) { setError((e as Error).message) }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Supplier quotes</h1>
          <p>
            Distributor quotes imported into the portal (one-off Meraki sales): the reference, the customer
            quote built from it, the margin chosen, the price-protection date and the original workbook.
            Their unit price is our cost — Wave9 internal users only.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop"><span>{error}</span><button className="btn btn--ghost btn--sm" onClick={() => setError(null)}>Dismiss</button></div>}

      <div className="card">
        <div className="card__head">
          <div className="toolbar">
            <input className="input" style={{ width: 300 }} placeholder="Search — reference, quote number, title or owner…"
              value={query} onChange={(e) => setQuery(e.target.value)} />
            <h2 style={{ margin: 0 }}>{shown.length} of {rows.length} — newest first</h2>
          </div>
          <span className="muted small">Imported from “+ New quote → From a distributor quote”.</span>
        </div>
        <div className="card__body">
          {shown.length === 0 && (
            <p className="muted small">
              {rows.length === 0
                ? 'Nothing yet. Import a distributor quote from “+ New quote” on the cockpit or the Quotes list (Wave9 partner, staff only).'
                : 'Nothing matches that search.'}
            </p>
          )}
          {shown.length > 0 && (
            <div className="table-wrap">
              <table data-testid="supplier-register">
                <thead>
                  <tr><th>Distributor · reference</th><th>Quote</th><th>Lines</th><th>Cost (ex VAT)</th><th>Margin</th><th>Price held to</th><th>Imported</th><th>Purchase order</th><th></th></tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const lapsed = r.priceProtectionUntil != null && r.priceProtectionUntil < today
                    return (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.distributor}</strong> {r.reference}
                          {r.ccwReference && <div className="muted small">CCW {r.ccwReference}</div>}
                        </td>
                        <td>
                          {r.quoteNumber
                            ? <><strong>{r.quoteNumber}</strong> <span className="pill">{r.quoteStatus}</span><div className="muted small">{r.quoteTitle}</div></>
                            : <span className="muted">quote deleted</span>}
                        </td>
                        <td>{r.lineCount}{r.productsCreated > 0 && <div className="muted small">{r.productsCreated} created in HubSpot</div>}</td>
                        <td>{gbp(r.totalExVat)}</td>
                        <td>{r.marginPct}%</td>
                        <td>
                          {r.priceProtectionUntil ? formatUKDate(r.priceProtectionUntil) : (r.priceProtectionText ?? '—')}
                          {lapsed && <> <span className="pill pill--warn" title={r.priceProtectionText ?? ''}>lapsed</span></>}
                        </td>
                        <td className="small">{new Date(r.createdAt).toLocaleDateString('en-GB')}<div className="muted">{r.createdBy}</div></td>
                        <td data-testid="po-cell">
                          {r.po
                            ? <>
                                <a href={r.po.url} target="_blank" rel="noreferrer"><strong>{r.po.xeroPoNumber ?? 'PO'}</strong></a>{' '}
                                <span className={`pill ${r.po.xeroStatus === 'DRAFT' ? 'pill--info' : r.po.xeroStatus === 'DELETED' ? 'pill--stop' : 'pill--ok'}`}>{r.po.xeroStatus}</span>
                                <div className="muted small">
                                  {gbp(r.po.subTotal)} · {new Date(r.po.updatedAt).toLocaleDateString('en-GB')} {r.po.updatedByName ?? r.po.raisedByName}
                                  {' · '}<button className="btn-link" disabled={checking === r.id} onClick={() => void checkStatus(r)} title="Re-read the status from Xero">{checking === r.id ? 'checking…' : 'check status'}</button>
                                </div>
                              </>
                            : r.quoteId ? <span className="muted">—</span> : <span className="muted">n/a</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {r.quoteId && (
                            <button className={`btn btn--sm ${r.po ? '' : 'btn--primary'}`} onClick={() => setPo(r)} data-testid="po-open"
                              title={r.po?.canUpdate ? 'Update the Xero draft with the quote as it is now' : r.po ? 'The PO is past draft in Xero — view only' : 'Raise a draft purchase order in Xero'}>
                              {r.po?.canUpdate ? 'Update PO' : r.po ? 'PO…' : 'Raise PO'}
                            </button>
                          )}{' '}
                          <button className="btn btn--sm" onClick={() => void openOne(r.id)}>Lines</button>{' '}
                          <a className="btn btn--sm btn--ghost" href={supplierQuoteFileUrl(r.id)} target="_blank" rel="noreferrer" title={r.filename}>📎 File</a>{' '}
                          {r.quoteId && <button className="btn btn--sm" disabled={!partner} onClick={() => onOpen(r.quoteId!)}>Open quote →</button>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {po && (
        <RaisePurchaseOrder supplierQuoteId={po.id} title={`${po.distributor} ${po.reference}`} onClose={() => setPo(null)}
          onRaised={(next) => setRowPo(po.id, next)} />
      )}

      {open && (
        <SlideOver title={`${open.record.distributor} ${open.record.reference}`} onClose={() => setOpen(null)} wide
          subtitle={`${open.record.quoteNumber ?? 'quote deleted'} · ${open.record.marginPct}% margin · ${open.record.currency}${open.record.exchangeRate ? ` @ ${open.record.exchangeRate}` : ''}${open.record.priceProtectionText ? ` · price protection ${open.record.priceProtectionText}` : ''}`}>
          <div className="table-wrap">
            <table data-testid="supplier-detail-lines">
              <thead>
                <tr><th>#</th><th>SKU</th><th>Description</th><th>Sheet qty</th><th>Unit cost</th><th>Quote qty</th><th>Quote sell</th><th>Product</th></tr>
              </thead>
              <tbody>
                {open.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="muted small">{l.lineRef}</td>
                    <td><code>{l.sku}</code></td>
                    <td>{l.description}{l.serviceMonths ? <div className="muted small">{l.serviceMonths} months</div> : null}{l.leadTime ? <div className="muted small">lead time {l.leadTime}</div> : null}</td>
                    <td>{l.qty}</td>
                    <td>{gbp(l.unitCost)}</td>
                    <td>{l.quoteQty ?? <span className="muted">line removed</span>}{l.quoteQty != null && l.quoteQty !== l.qty && <span className="pill pill--info" style={{ marginLeft: 4 }}>changed</span>}</td>
                    <td>{gbp(l.quoteSell)}</td>
                    <td>{l.productCreated ? <span className="pill pill--warn">created</span> : l.hsProductId ? <span className="pill pill--ok">catalogue</span> : <span className="pill pill--info">ad-hoc</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small" style={{ marginTop: 10 }}>
            Sheet quantities and costs are as imported; the quote columns are live. <a href={supplierQuoteFileUrl(open.record.id)} target="_blank" rel="noreferrer">Download {open.record.filename}</a>.
          </p>
        </SlideOver>
      )}
    </>
  )
}
